import React, { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmotionLevel } from './EmotionSelector';
import { getEmotionColor } from '@/utils/emotionHelpers';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { X } from "lucide-react";

interface StageHeaderProps {
  title: string;
  index: number;
  emotion: EmotionLevel;
  onTitleChange?: (title: string) => void;
}

const StageHeader: React.FC<StageHeaderProps> = ({ 
  title, 
  index, 
  emotion,
  onTitleChange 
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title || "");
  
  const handleSaveTitle = () => {
    if (onTitleChange) onTitleChange(tempTitle);
    setIsEditingTitle(false);
  };

  const handleClearTitle = () => {
    setTempTitle('');
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditingTitle) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleSaveTitle();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        setTempTitle(title || "");
        setIsEditingTitle(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditingTitle, tempTitle, title, onTitleChange]);
  
  // Check if title is the default one or has been customized
  const isDefaultTitle = title === `Stage ${index + 1}` || !title;
  
  return (
    <div className="flex flex-row items-start justify-between">
      <div className="flex-1">
        {isEditingTitle ? (
          <div className="relative">
            <Input
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              placeholder="E.g., Discovery, Research, Onboarding, First Use"
              className="text-base h-7 py-1"
              autoFocus
            />
            {tempTitle && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 absolute top-1 right-1 p-0"
                onClick={handleClearTitle}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Clear</span>
              </Button>
            )}
            <div className="flex justify-start mt-1 gap-1">
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-6 text-xs"
                onClick={() => setIsEditingTitle(false)}
              >
                Cancel
              </Button>
              <Button 
                size="sm" 
                className="h-6 text-xs"
                onClick={handleSaveTitle}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <h3 
                  className={cn(
                    "font-medium text-lg line-clamp-1 cursor-pointer hover:bg-muted/40 p-1 rounded",
                    isDefaultTitle ? "text-muted-foreground" : "text-foreground"
                  )}
                  onClick={() => {
                    setTempTitle(title || "");
                    setIsEditingTitle(true);
                  }}
                >
                  {title || "Click to add stage title"}
                </h3>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Name this stage of the user journey</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className={cn(
        "w-3 h-3 rounded-full mt-1", 
        getEmotionColor(emotion)
      )} />
    </div>
  );
};

export default StageHeader;
